/**
 * Card Match round scene — the main board view.
 *
 * Lays out the grid using CSS grid; each tile is a card with two faces
 * (front showing the symbol art, back showing the brand card-back). The CSS
 * 3D-flip is driven by toggling a `.cm-tile-flipped` class on the card root.
 *
 * Tiles update via `setRound(state)` — caller passes the latest reducer
 * state; we diff against the last render and only update changed tiles.
 *
 * BARE-MINIMUM behaviour (intentionally no FX):
 *   - face art = SVG icon (per `iconForSymbol`); falls back to raw text
 *   - card back = brand-gradient panel with the grid number centered
 *   - match: tile stays face-up, gets a solid green border via `.cm-tile-matched`
 *   - mismatch: tile flips back face-down via the standard rotateY transition
 *   - NO shake, NO pulse, NO particles, NO score popup
 *
 * Stylesheet lives in `./round-styles.ts`.
 */

import type { RoundState, TileViewState } from '../state/types.js';
import { iconForSymbol } from './lib/icons.js';
import { ensureRoundStyles } from './round-styles.js';
import { STAGE_W, STAGE_H } from './lib/stage.js';

/**
 * Stage-constant pixel reserves for the round layout. Must match the values
 * baked into round-styles.ts so the board sits at predictable coordinates
 * regardless of any sibling DOM (header line-height, footer wrapping, etc.).
 *
 * Why constants instead of measured DOM:
 *   The previous implementation read boardEl.getBoundingClientRect() and a
 *   ResizeObserver re-fired the tile-size calc whenever the parent grid's
 *   1fr row changed. Web-font swaps (Oswald/Bebas Neue arriving late),
 *   leaderboard cross-fades, and round-splash mount/unmount could all
 *   nudge the 1fr cell by a pixel or two — enough to recompute
 *   --cm-tile-size and visibly resize every tile mid-round.
 *   Tying tile size to fixed stage geometry makes the layout deterministic.
 */
const BOARD_PAD_LEFT = 80;
const BOARD_PAD_RIGHT = 460;
const BOARD_PAD_TOP = 156; // 36px top pad + 120px reserved header band.
const BOARD_PAD_BOTTOM = 100; // 24px bottom pad + 76px reserved footer band.
const BOARD_GAP_PX = 12;

export interface RoundHandle {
  setVisible(v: boolean): void;
  setRound(r: RoundState): void;
  /** Drive the countdown ring + remaining-time label from outside (200ms). */
  tick(): void;
  dispose(): void;
}

const ROOT_ID = 'cm-round';

export function createRound(stage: HTMLElement): RoundHandle {
  ensureRoundStyles(ROOT_ID);
  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.setAttribute('data-overlay', 'round');
  root.innerHTML = `
    <div class="cm-round-grid">
      <header class="cm-round-header">
        <div class="cm-round-tag" data-cm-round-tag>ROUND 1</div>
        <div class="cm-round-timer" data-cm-round-timer>00:00</div>
        <div class="cm-round-pairs" data-cm-round-pairs>0 / 0</div>
      </header>

      <div class="cm-round-board" data-cm-round-board></div>

      <footer class="cm-round-footer">
        <span class="cm-round-help">Type two grid numbers to flip a pair <span class="cm-round-help-example">e.g. 3 7</span></span>
        <span class="cm-round-flip" data-cm-round-flip></span>
      </footer>
    </div>
  `;
  stage.appendChild(root);

  let lastRoundIndex = -1;
  let lastTotalTiles = -1;
  let lastTiles: TileViewState[] | null = null;
  let lastFlipKey = '';
  let currentRound: RoundState | null = null;

  const boardEl = root.querySelector('[data-cm-round-board]') as HTMLDivElement;

  /**
   * Compute the per-tile pixel size from STAGE CONSTANTS — not measured DOM.
   *
   * Why constants:
   *   The stage is a fixed 1920x1080 design surface (scaled to viewport via
   *   transform). Reserving fixed pixel bands for the header (120px) and
   *   footer (76px) means the board area is always the same size regardless
   *   of font-loading state, leaderboard cross-fades, or splash mount/unmount.
   *   This eliminates the resize cascade that previously caused tiles to
   *   visibly jump mid-round when web fonts swapped or sibling DOM reflowed.
   */
  function recomputeTileSize(): void {
    if (!currentRound) return;
    const cols = currentRound.width;
    const rows = currentRound.height;
    const availW = STAGE_W - BOARD_PAD_LEFT - BOARD_PAD_RIGHT - BOARD_GAP_PX * (cols - 1);
    const availH = STAGE_H - BOARD_PAD_TOP - BOARD_PAD_BOTTOM - BOARD_GAP_PX * (rows - 1);
    const size = Math.max(40, Math.floor(Math.min(availW / cols, availH / rows)));
    boardEl.style.setProperty('--cm-tile-size', `${size}px`);
  }

  function setVisible(v: boolean): void {
    root.style.display = v ? '' : 'none';
    if (v) recomputeTileSize();
  }

  function setRound(r: RoundState): void {
    currentRound = r;
    if (r.index !== lastRoundIndex || r.totalTiles !== lastTotalTiles) {
      lastRoundIndex = r.index;
      lastTotalTiles = r.totalTiles;
      buildGrid(boardEl, r);
      lastTiles = r.tiles;
      lastFlipKey = '';
      // Reset bubble-linger state so a stale bubble from a previous round
      // doesn't persist into the new round's first frame.
      if (bubbleClearTimer) {
        clearTimeout(bubbleClearTimer);
        bubbleClearTimer = null;
      }
      lastBubbleKey = '';
      recomputeTileSize();
    } else {
      updateTiles(boardEl, r.tiles, lastTiles);
      lastTiles = r.tiles;
    }
    paintHud(root, r);
    // No flip FX — bare-minimum behaviour. The reducer already drives the
    // tile flip via .cm-tile-flipped (peeking || matched) in updateTiles().
    // lastFlipKey kept only to preserve the diff-skip pattern in setRound.
    if (r.flip) {
      lastFlipKey = `${r.flip.positions[0]}-${r.flip.positions[1]}-${r.flip.peekEndsAtMs}`;
    }
  }

  function tick(): void {
    if (!currentRound) return;
    const remainingMs = Math.max(0, currentRound.endsAtMs - Date.now());
    const totalSec = Math.floor(remainingMs / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    const timer = root.querySelector('[data-cm-round-timer]');
    if (timer) {
      const padded = `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
      timer.textContent = padded;
      if (totalSec <= 5) {
        (timer as HTMLElement).classList.add('cm-round-timer-urgent');
      } else {
        (timer as HTMLElement).classList.remove('cm-round-timer-urgent');
      }
    }
  }

  // No ResizeObserver: tile size is derived from stage constants, not from
  // live DOM measurements. The DOM stage already handles viewport scaling
  // via a single CSS transform on its outer container, so per-tile pixels
  // stay deterministic across font swaps, splash mount/unmount, leaderboard
  // cross-fades, and any other sibling reflow.

  return {
    setVisible,
    setRound,
    tick,
    dispose() {
      root.remove();
    },
  };
}

/**
 * Hold the chat-guess bubble visible for this long AFTER the round state
 * clears r.flip. Reducer clears r.flip the moment the peek expires (so
 * tiles can flip back face-down), but the bubble text should linger a
 * little so a viewer's eye has time to read who just guessed and whether
 * it matched.
 */
const BUBBLE_LINGER_AFTER_FLIP_MS = 800;

/** Module-scoped so each scene instance owns its own bubble-linger state.
 *  paintHud is called from setRound() per render — we coalesce duplicate
 *  bubble updates and only schedule a clear-timeout once per flip event. */
let bubbleClearTimer: ReturnType<typeof setTimeout> | null = null;
let lastBubbleKey = '';

function paintHud(root: HTMLElement, r: RoundState): void {
  const tag = root.querySelector('[data-cm-round-tag]');
  if (tag) tag.textContent = `ROUND ${r.index + 1}`;
  const pairs = root.querySelector('[data-cm-round-pairs]');
  if (pairs) {
    const matched = r.tiles.filter((t) => t.matched).length / 2;
    pairs.textContent = `${matched} / ${r.pairCount}`;
  }
  const flipEl = root.querySelector('[data-cm-round-flip]') as HTMLElement | null;
  if (!flipEl) return;
  if (r.flip) {
    // New flip → paint, cancel any pending clear, remember it for the
    // post-peek linger.
    const key = `${r.flip.positions[0]}-${r.flip.positions[1]}-${r.flip.peekEndsAtMs}`;
    if (key !== lastBubbleKey) {
      lastBubbleKey = key;
      const matchTxt = r.flip.match
        ? `MATCH +${r.flip.pointsAwarded ?? 0}`
        : 'no match';
      flipEl.textContent = `${escapeHtml(r.flip.displayName)}: ${r.flip.positions[0]}×${r.flip.positions[1]} ${matchTxt}`;
      flipEl.className = `cm-round-flip ${r.flip.match ? 'match' : 'miss'}`;
      if (bubbleClearTimer) {
        clearTimeout(bubbleClearTimer);
        bubbleClearTimer = null;
      }
    }
  } else if (lastBubbleKey && !bubbleClearTimer) {
    // Round state has cleared the flip (peek expired). Don't snap the
    // bubble away — schedule a linger so viewers can still read it.
    bubbleClearTimer = setTimeout(() => {
      flipEl.textContent = '';
      flipEl.className = 'cm-round-flip';
      lastBubbleKey = '';
      bubbleClearTimer = null;
    }, BUBBLE_LINGER_AFTER_FLIP_MS);
  }
}

function buildGrid(board: HTMLDivElement, r: RoundState): void {
  board.innerHTML = '';
  // Use --cm-tile-size (pixel) for both columns and rows so tiles are
  // pixel-perfect squares regardless of board container aspect ratio. The
  // grid container centers the resulting block via place-content: center.
  board.style.gridTemplateColumns = `repeat(${r.width}, var(--cm-tile-size, 100px))`;
  board.style.gridTemplateRows = `repeat(${r.height}, var(--cm-tile-size, 100px))`;
  // Card-back design: number-only over a brand gradient. The number IS the
  // back — viewers care about typing the right grid number, not seeing a
  // brand mark on every tile. Brand identity lives in the lobby + side-LB.
  for (const tile of r.tiles) {
    const card = document.createElement('div');
    card.className = 'cm-tile';
    card.dataset.position = String(tile.position);
    card.innerHTML = `
      <div class="cm-tile-inner">
        <div class="cm-tile-face cm-tile-back">
          <span class="cm-tile-back-num">${tile.position}</span>
        </div>
        <div class="cm-tile-face cm-tile-front">
          <img class="cm-tile-symbol-img" alt="" draggable="false" hidden/>
          <span class="cm-tile-symbol"></span>
        </div>
      </div>
    `;
    board.appendChild(card);
  }
  updateTiles(board, r.tiles, null);
}

function updateTiles(
  board: HTMLDivElement,
  tiles: TileViewState[],
  prev: TileViewState[] | null,
): void {
  for (let i = 0; i < tiles.length; i++) {
    const t = tiles[i]!;
    const p = prev ? prev[i] : null;
    if (p && p.symbol === t.symbol && p.matched === t.matched && p.peeking === t.peeking) {
      continue;
    }
    const el = board.children[i] as HTMLElement | undefined;
    if (!el) continue;
    paintFace(el, t);
    el.classList.toggle('cm-tile-flipped', t.peeking || t.matched);
    el.classList.toggle('cm-tile-matched', t.matched);
  }
}

/**
 * Apply the tile's current symbol to the front face WITHOUT ever clearing
 * a previously-set src.
 *
 * Why: Chrome (and other browsers) CANCEL in-flight network requests when
 * you call `img.removeAttribute('src')` or set `img.hidden = true`. The
 * previous implementation cleared the src + set hidden whenever the
 * reducer flipped a tile back face-down (peek expired → symbol=''), which
 * killed the in-flight SVG fetch mid-animation. Result: viewers saw a
 * blank front face during the peek window.
 *
 * The 3D flip uses `backface-visibility: hidden` on each face, so a
 * front face with a loaded img is invisible while the tile is face-down.
 * Once we set the src, we LEAVE IT — the browser keeps the img element
 * alive, the request completes, and every subsequent peek shows the
 * symbol instantly.
 *
 * Why we never need to "clear" the symbol from the DOM:
 *   - For a given tile position, the underlying symbol is fixed for the
 *     entire round (the same card lives there).
 *   - When a new round opens, `buildGrid` does `innerHTML = ''` and the
 *     img elements are recreated from scratch (no stale src).
 *   - Backend-supplied symbols only arrive via cm_tile_flip /
 *     cm_tile_reveal_permanent / cm_round_end with full_board — and they
 *     always agree on the value for any given position within the round.
 */
function paintFace(el: HTMLElement, t: TileViewState): void {
  const img = el.querySelector('.cm-tile-symbol-img') as HTMLImageElement | null;
  const txt = el.querySelector('.cm-tile-symbol') as HTMLSpanElement | null;
  if (!img || !txt) return;
  if (!t.symbol) {
    // No symbol arrived yet for this tile (initial face-down). Leave the
    // img alone. If a previous flip set src already, we keep it loaded so
    // future peeks show it instantly.
    return;
  }
  const url = iconForSymbol(t.symbol);
  if (url) {
    if (img.getAttribute('src') !== url) img.src = url;
    img.hidden = false;
    txt.textContent = '';
  } else {
    // Non-codepoint symbol (legacy / fallback) — render as text. We don't
    // touch img.src here either; the previous img stays loaded but the
    // text overlays it.
    txt.textContent = t.symbol;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
